/**
 * المرحلة 1.3 (F10) — the doctor's queue of requests: «معلقة»، «قادمة»،
 * «مكتملة» (the plan's screens 7–8).
 *
 * GET /api/bookings/queue: the doctor's own (scope doctor) or, at clinics
 * reception, every doctor of the institution (scope institution — the same
 * component is a tab of OperationsBoard). A request is accepted, or declined
 * with a reason the patient reads on their appointments and their bell. A
 * confirmed visit that is due shows «ابدأ», which opens the live session
 * (the call itself, once 1.5 brings it).
 *
 * Under the default policy (doctorQueue.confirm «auto») a patient's booking
 * is confirmed at once unless a deposit waits, so «معلقة» holds exactly what
 * it held before: bookings whose deposit the clinic still has to review. The
 * deposit's own screens are unchanged and still confirm it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, EmptyState, ErrorState, Field, Icon, Modal, PageHeader, Panel, Spinner, Tabs, Textarea } from '../../components/common';
import { bookingApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { formatTime12 } from '../../domain/dates';
import { formatCurrency } from '../../domain/pricing';
import { useI18n } from '../../i18n/I18nProvider';
import { ModeBadge, dayLabel } from '../discovery/parts';

const POLL_MS = 30000;

export function DoctorQueue({ scope = 'doctor', embedded = false }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [tab, setTab] = useState('pending');
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [busy, setBusy] = useState(null);
  const [declining, setDeclining] = useState(null);

  const load = useCallback(
    (quiet = false) => {
      if (!quiet) setState((s) => ({ ...s, loading: true, error: null }));
      return bookingApi
        .queue(scope)
        .then((data) => setState({ loading: false, error: null, data }))
        .catch((error) => setState((s) => ({ loading: false, error: quiet && s.data ? null : error, data: s.data })));
    },
    [scope],
  );

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const data = state.data || { pending: [], upcoming: [], completed: [] };
  const tabs = useMemo(
    () => [
      { id: 'pending', label: t('queue.tab.pending'), icon: 'inbox', count: data.pending.length },
      { id: 'upcoming', label: t('queue.tab.upcoming'), icon: 'calendar-check', count: data.upcoming.length },
      { id: 'completed', label: t('queue.tab.completed'), icon: 'check-circle-2', count: data.completed.length },
    ],
    [t, data.pending.length, data.upcoming.length, data.completed.length],
  );

  const accept = async (booking) => {
    setBusy(booking.id);
    try {
      await bookingApi.accept(booking.id);
      success(t('queue.accepted', { name: booking.patientName || '' }));
      load(true);
    } catch (err) {
      toastError(err.body?.code === 'NOT_PENDING' ? t('queue.alreadyAnswered') : err.message);
      load(true);
    } finally {
      setBusy(null);
    }
  };

  const decline = async (reason) => {
    const booking = declining;
    setBusy(booking.id);
    try {
      await bookingApi.decline(booking.id, reason);
      success(t('queue.declined'));
      setDeclining(null);
      load(true);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const list = data[tab] || [];

  return (
    <div className="space-y-5" data-testid="doctor-queue">
      {!embedded && (
        <PageHeader
          title={t('queue.title')}
          subtitle={state.data?.policy?.confirm === 'doctor' ? t('queue.subtitleDoctor') : t('queue.subtitleAuto')}
          icon="inbox"
          actions={
            <Button variant="ghost" icon="refresh-cw" onClick={() => load()} loading={state.loading}>
              {t('queue.refresh')}
            </Button>
          }
        />
      )}

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {state.error ? (
        <ErrorState error={state.error.message} onRetry={() => load()} />
      ) : state.loading && !state.data ? (
        <Spinner label={t('queue.loading')} />
      ) : list.length === 0 ? (
        <Panel>
          <EmptyState icon={tab === 'pending' ? 'inbox' : 'calendar-x'} title={t(`queue.empty.${tab}`)} message={t(`queue.emptyHint.${tab}`)} />
        </Panel>
      ) : (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          {list.map((booking) => (
            <QueueCard
              key={booking.id}
              booking={booking}
              tab={tab}
              scope={scope}
              busy={busy === booking.id}
              onAccept={() => accept(booking)}
              onDecline={() => setDeclining(booking)}
            />
          ))}
        </div>
      )}

      <DeclineDialog booking={declining} busy={Boolean(declining) && busy === declining.id} onClose={() => setDeclining(null)} onConfirm={decline} />
    </div>
  );
}

function QueueCard({ booking, tab, scope, busy, onAccept, onDecline }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const depositWaiting = booking.depositRequired && !booking.depositConfirmed;
  const lapses = booking.lapsesAt ? new Date(booking.lapsesAt) : null;
  const open = ['pending', 'confirmed'].includes(booking.status);

  return (
    <div
      className={`queue-card ${tab === 'upcoming' ? 'queue-card--upcoming' : tab === 'completed' ? 'queue-card--completed' : ''} ${booking.due ? 'queue-card--due' : ''}`}
      data-testid="queue-card"
    >
      <div className="queue-card__head">
        <span className="queue-card__when">
          <Icon name="calendar-days" size={14} />
          {dayLabel(booking.date, lang, { weekday: 'short', day: 'numeric', month: 'short' })} · {formatTime12(booking.time)}
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          <ModeBadge mode={booking.consultMode || 'in_person'} />
          {booking.due && <Badge className="border-emerald-500/40 bg-emerald-500/15 text-emerald-300">{t('queue.due')}</Badge>}
        </span>
      </div>

      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-white">{booking.patientName || t('queue.patient')}</p>
        <p className="truncate text-xs text-slate-400">
          {[scope === 'institution' ? booking.doctorName : null, booking.clinicName, booking.type === 'followup' ? t('queue.followup') : t('queue.exam'), booking.finalPrice ? formatCurrency(booking.finalPrice) : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      {tab === 'pending' && depositWaiting && (
        <p className="info-note">
          <Icon name="wallet" size={14} className="mt-0.5 shrink-0" />
          <span>{booking.depositReceipt ? t('queue.depositReceipt') : t('queue.depositAwaited')}</span>
        </p>
      )}
      {tab === 'pending' && lapses && <p className="queue-lapse">{t('queue.lapses', { time: lapses.toLocaleString(lang === 'en' ? 'en-GB' : 'ar-EG-u-nu-latn', { hour: 'numeric', minute: '2-digit', day: 'numeric', month: 'short' }) })}</p>}
      {Array.isArray(booking.rescheduledFrom) && booking.rescheduledFrom.length > 0 && (
        <p className="text-[11px] font-bold text-cyan-300">
          <Icon name="calendar-clock" size={12} className="me-1 inline" />
          {t('queue.moved', { day: dayLabel(booking.rescheduledFrom[booking.rescheduledFrom.length - 1].date, lang, { day: 'numeric', month: 'short' }), time: formatTime12(booking.rescheduledFrom[booking.rescheduledFrom.length - 1].time) })}
        </p>
      )}

      {tab !== 'completed' && open && (
        <div className="queue-card__actions">
          <Button variant="ghost" icon="x" onClick={onDecline} disabled={busy} data-testid="queue-decline">
            {t('queue.decline')}
          </Button>
          {booking.status === 'pending' && (
            <Button variant="success" icon="check" onClick={onAccept} loading={busy} data-testid="queue-accept">
              {t('queue.accept')}
            </Button>
          )}
          {booking.due && scope === 'doctor' && (
            <Button icon="play" onClick={() => navigate('/doctor/live')} data-testid="queue-start">
              {t('queue.start')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** The reason is required: the patient reads it. */
function DeclineDialog({ booking, busy, onClose, onConfirm }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  useEffect(() => setReason(''), [booking]);
  if (!booking) return null;
  const suggestions = ['travel', 'emergency', 'full', 'referral'];
  return (
    <Modal
      open={Boolean(booking)}
      onClose={onClose}
      title={t('queue.declineTitle', { name: booking.patientName || '' })}
      icon="calendar-x"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="send" onClick={() => onConfirm(reason.trim())} disabled={!reason.trim()} loading={busy} data-testid="queue-decline-confirm">
            {t('queue.declineSend')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-xs leading-relaxed text-slate-400">{t('queue.declineHint')}</p>
        <div className="dc-chips">
          {suggestions.map((s) => (
            <button key={s} type="button" className="dc-chip" onClick={() => setReason(t(`queue.reasons.${s}`))}>
              {t(`queue.reasons.${s}`)}
            </button>
          ))}
        </div>
        <Field label={t('queue.reason')} required>
          <Textarea rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="queue-reason" />
        </Field>
        {booking.depositPaid && Number(booking.depositAmount) > 0 && (
          <p className="info-note">
            <Icon name="wallet" size={14} className="mt-0.5 shrink-0" />
            <span>{t('queue.declineRefund', { amount: formatCurrency(booking.depositAmount) })}</span>
          </p>
        )}
      </div>
    </Modal>
  );
}

export default DoctorQueue;
